import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Tag, Store, Loader2, Pencil, AlertTriangle } from "lucide-react";
import { MemberPicker, MemberChips } from "@/components/MemberPicker";
import { toast } from "sonner";

const EMPTY_CAT = { name: "", group: "Annexes", type: "expense", scope: "monthly", default_member_ids: [] };

export default function Vendors() {
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [pattern, setPattern] = useState("");
  const [catId, setCatId] = useState("");
  const [adding, setAdding] = useState(false);
  const [catDialog, setCatDialog] = useState(false);
  const [editId, setEditId] = useState(null);
  const [newCat, setNewCat] = useState(EMPTY_CAT);
  const [delDialog, setDelDialog] = useState(false);
  const [delCat, setDelCat] = useState(null);
  const [delUsage, setDelUsage] = useState(null);
  const [replaceId, setReplaceId] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/vendors").then((r) => setVendors(r.data.sort((a, b) => a.pattern.localeCompare(b.pattern))));
    api.get("/family-members").then((r) => setMembers(r.data));
  };
  useEffect(load, []);

  const addRule = async () => {
    if (!pattern.trim() || !catId) { toast.error("Renseignez un prestataire et une catégorie"); return; }
    setAdding(true);
    try {
      await api.post("/vendors", { pattern, category_id: catId });
      toast.success("Règle enregistrée");
      setPattern(""); setCatId(""); load();
    } catch { toast.error("Erreur"); } finally { setAdding(false); }
  };

  const delRule = async (id) => { await api.delete(`/vendors/${id}`); load(); toast.success("Règle supprimée"); };

  const changeRuleCategory = async (v, newCatId) => {
    await api.post("/vendors", { pattern: v.pattern, category_id: newCatId });
    toast.success("Règle mise à jour"); load();
  };

  const saveCategory = async () => {
    if (!newCat.name.trim()) { toast.error("Nom requis"); return; }
    try {
      if (editId) await api.put(`/categories/${editId}`, newCat);
      else await api.post("/categories", newCat);
      toast.success(editId ? "Catégorie modifiée" : "Catégorie créée"); setCatDialog(false);
      setNewCat(EMPTY_CAT); setEditId(null); load();
    } catch { toast.error("Erreur"); }
  };

  const openNewCat = () => { setNewCat(EMPTY_CAT); setEditId(null); setCatDialog(true); };
  const openEditCat = (c) => { setNewCat({ name: c.name, group: c.group, type: c.type, scope: c.scope, default_member_ids: c.default_member_ids || [] }); setEditId(c.id); setCatDialog(true); };

  const delCategory = async (c) => {
    try {
      const { data } = await api.get(`/categories/${c.id}/usage`);
      if (!data.in_use) {
        await api.delete(`/categories/${c.id}`);
        toast.success("Catégorie supprimée"); load(); return;
      }
      setDelCat(c); setDelUsage(data); setReplaceId(""); setDelDialog(true);
    } catch { toast.error("Erreur lors de la vérification de la catégorie"); }
  };

  const confirmDelete = async () => {
    if (!replaceId) { toast.error("Choisissez une catégorie de remplacement"); return; }
    setDeleting(true);
    try {
      await api.delete(`/categories/${delCat.id}?replace_with=${replaceId}`);
      toast.success("Catégorie remplacée puis supprimée");
      setDelDialog(false); setDelCat(null); setDelUsage(null); load();
    } catch { toast.error("Erreur lors du remplacement"); } finally { setDeleting(false); }
  };

  const groups = [...new Set(categories.map((c) => c.group))];
  const byGroup = groups.map((g) => ({ group: g, items: categories.filter((c) => c.group === g) }));

  return (
    <div className="space-y-6" data-testid="vendors-page">
      <div>
        <h1 className="font-display font-bold text-3xl tracking-tight">Prestataires & Catégories</h1>
        <p className="text-muted-foreground text-sm mt-1">Entraînez la catégorisation automatique et gérez vos catégories.</p>
      </div>

      <Tabs defaultValue="vendors">
        <TabsList>
          <TabsTrigger value="vendors" data-testid="tab-vendors"><Store className="w-4 h-4 mr-1.5" />Prestataires</TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories"><Tag className="w-4 h-4 mr-1.5" />Catégories</TabsTrigger>
        </TabsList>

        <TabsContent value="vendors" className="space-y-4">
          <Card className="p-5 border-border">
            <h3 className="font-display font-semibold text-base mb-1">Nouvelle règle</h3>
            <p className="text-xs text-muted-foreground mb-4">Quand le nom du prestataire contient ce texte, la catégorie sera appliquée automatiquement lors des imports.</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 space-y-1.5"><Label>Prestataire contient</Label><Input value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="Ex: totalenergies" data-testid="vendor-pattern-input" /></div>
              <div className="flex-1 space-y-1.5"><Label>Catégorie</Label>
                <Select value={catId} onValueChange={setCatId}>
                  <SelectTrigger data-testid="vendor-category-select"><SelectValue placeholder="Choisir" /></SelectTrigger>
                  <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.group}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-end"><Button onClick={addRule} disabled={adding} data-testid="add-vendor-rule-button">{adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" />Ajouter</>}</Button></div>
            </div>
          </Card>

          <Card className="p-5 border-border overflow-hidden">
            <h3 className="font-display font-semibold text-base mb-3">Règles enregistrées ({vendors.length})</h3>
            {vendors.length === 0 ? <p className="text-sm text-muted-foreground py-6 text-center">Aucune règle. Elles sont aussi créées automatiquement quand vous catégorisez un import.</p> : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Prestataire (contient)</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {vendors.map((v) => (
                    <TableRow key={v.id || v.pattern} data-testid={`vendor-rule-row-${v.pattern}`}>
                      <TableCell className="font-medium">{v.pattern}</TableCell>
                      <TableCell>
                        <Select value={v.category_id} onValueChange={(val) => changeRuleCategory(v, val)}>
                          <SelectTrigger className="h-8 text-xs w-full max-w-xs" data-testid={`vendor-edit-cat-${v.pattern}`}><SelectValue /></SelectTrigger>
                          <SelectContent>{categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.group}</SelectItem>)}</SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => delRule(v.id)} data-testid={`delete-vendor-${v.pattern}`}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={openNewCat} data-testid="add-category-button"><Plus className="w-4 h-4 mr-1.5" />Nouvelle catégorie</Button>
          </div>
          {byGroup.map((g) => (
            <Card key={g.group} className="p-5 border-border">
              <h3 className="font-display font-semibold text-sm mb-3 flex items-center gap-2">{g.group}<Badge variant="secondary" className="font-normal">{g.items.length}</Badge></h3>
              <div className="flex flex-wrap gap-2">
                {g.items.map((c) => (
                  <div key={c.id} className="flex items-center gap-2 bg-secondary rounded-full pl-3 pr-1.5 py-1" data-testid={`category-chip-${c.id}`}>
                    <span className="text-sm">{c.name}</span>
                    {c.default_member_ids && c.default_member_ids.length > 0 && <MemberChips members={members} ids={c.default_member_ids} />}
                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => openEditCat(c)} data-testid={`edit-category-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="icon" className="h-6 w-6 text-destructive" onClick={() => delCategory(c)} data-testid={`delete-category-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Modifier la catégorie" : "Nouvelle catégorie"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nom</Label><Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} data-testid="new-category-name" /></div>
            <div className="space-y-1.5"><Label>Groupe</Label>
              <Select value={newCat.group} onValueChange={(v) => setNewCat({ ...newCat, group: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{groups.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={newCat.type} onValueChange={(v) => setNewCat({ ...newCat, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="expense">Dépense</SelectItem><SelectItem value="income">Revenu</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Fréquence</Label>
                <Select value={newCat.scope} onValueChange={(v) => setNewCat({ ...newCat, scope: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="monthly">Mensuel</SelectItem><SelectItem value="annual">Annuel</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Membres par défaut <span className="text-muted-foreground font-normal text-xs">(pré-remplis à chaque dépense de cette catégorie)</span></Label>
              <MemberPicker members={members} value={newCat.default_member_ids} onChange={(v) => setNewCat({ ...newCat, default_member_ids: v })} testid="cat-member-picker" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCatDialog(false)}>Annuler</Button><Button onClick={saveCategory} data-testid="save-category-button">{editId ? "Enregistrer" : "Créer"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delDialog} onOpenChange={setDelDialog}>
        <DialogContent data-testid="delete-category-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-destructive" />Catégorie utilisée</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              La catégorie <span className="font-semibold text-foreground">« {delCat?.name} »</span> est encore utilisée.
              Choisissez par quelle catégorie la remplacer : tous les éléments liés y seront réaffectés avant la suppression.
            </p>
            {delUsage && (
              <div className="rounded-lg bg-secondary/50 p-3 text-sm space-y-1" data-testid="delete-usage-summary">
                {delUsage.counts.transactions > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Mouvements</span><span className="font-num">{delUsage.counts.transactions}</span></div>}
                {delUsage.counts.budgets > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Budgets (mois type / mensuels / annuels)</span><span className="font-num">{delUsage.counts.budgets}</span></div>}
                {delUsage.counts.planned > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Éléments exceptionnels prévus</span><span className="font-num">{delUsage.counts.planned}</span></div>}
                {delUsage.counts.rules > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Règles de prestataire</span><span className="font-num">{delUsage.counts.rules}</span></div>}
                {delUsage.counts.properties > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Biens / emprunts liés</span><span className="font-num">{delUsage.counts.properties}</span></div>}
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Remplacer par</Label>
              <Select value={replaceId} onValueChange={setReplaceId}>
                <SelectTrigger data-testid="replace-category-select"><SelectValue placeholder="Choisir une catégorie" /></SelectTrigger>
                <SelectContent>
                  {categories.filter((c) => delCat && c.type === delCat.type && c.id !== delCat.id)
                    .map((c) => <SelectItem key={c.id} value={c.id}>{c.name} · {c.group}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">Seules les catégories du même type ({delCat?.type === "income" ? "revenu" : "dépense"}) sont proposées.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelDialog(false)}>Annuler</Button>
            <Button variant="destructive" onClick={confirmDelete} disabled={deleting || !replaceId} data-testid="confirm-delete-category-button">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Remplacer et supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
